import React, { useContext } from 'react';
import { ChipStyled, TypographyBold } from 'share/component_css';
import { Box, Typography, Chip } from '@material-ui/core';
import { formatDate, surveyColorTag, renderOutline } from 'share/utils';
import { SurveyResultEmbeddedFragment } from 'types.d';
import { PatientDetailContext } from 'share/context';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { IvrSurveyPhase } from 'modules/patients/interfaces';
import { useIvrCall } from './ivrCalls';
import IvrPhaseChip, { ivrPhaseColor } from './IvrPhaseChip';
import { useCustomPermission } from 'hooks';

type Props = {
  result: SurveyResultEmbeddedFragment;
  editSurvey: (isUpdateSurvey?: boolean) => void;
  detailSurveyResult: React.MutableRefObject<
    SurveyResultEmbeddedFragment | undefined
  >;
  open: (
    event: React.MouseEvent<HTMLElement | SVGSVGElement, MouseEvent>,
  ) => void;
};

export const SurveyResult: React.FC<Props> = ({
  result,
  editSurvey,
  detailSurveyResult,
  open,
}) => {
  const patientDetailContext = useContext(PatientDetailContext);

  const { isAdmin } = useCustomPermission();

  const ivrCall = useIvrCall(result._id ? String(result._id) : undefined);

  // A phone survey mid-call already has answers, and both `filled` and
  // `navigatorFilled` render green -- so an in-flight call is otherwise
  // indistinguishable from a finished one. Let the phase drive the colour
  // until the call has actually completed.
  const backgroundcolor =
    ivrCall && ivrCall.phase !== IvrSurveyPhase.Completed
      ? ivrPhaseColor(ivrCall.phase) || surveyColorTag(result)
      : surveyColorTag(result);

  return (
    <ChipStyled
      opacity={result.isArchive ? 'true' : ''}
      className={`cursor-pointer ${renderOutline(result)}`}
      label={
        <Box display="flex" alignItems="center">
          <TypographyBold className="mr-8" variant="subtitle2">
            {formatDate(result.createdAt, true)}
          </TypographyBold>
          <Typography className="ml-8" variant="subtitle2">
            {`Updated ${formatDate(result.updatedAt, true)} ${
              result.updatedBy
                ? `, ${result.updatedBy.firstName} ${result.updatedBy
                    .middleName || ''} ${result.updatedBy.lastName}`
                : ''
            }`}
          </Typography>
          {ivrCall && <IvrPhaseChip call={ivrCall} />}
          {isAdmin && result.createdByExternal && (
            <Chip className="ml-8" label="DME" size="small" color="secondary" />
          )}
        </Box>
      }
      onClick={e => {
        detailSurveyResult.current = result;
        editSurvey(true);
      }}
      onDelete={e => {
        open(e);
        detailSurveyResult.current = result;
      }}
      deleteIcon={
        !patientDetailContext?.isInactive ? (
          <MoreHorizIcon className="m-8" />
        ) : (
          <></>
        )
      }
      backgroundcolor={backgroundcolor}
    />
  );
};

export default React.memo(SurveyResult);
